import React, { useEffect, useState } from "react";
import { taskDetailApi } from "../../backend/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  TaskDialog,
  TaskDialogActions,
  TaskField,
  TaskMessage,
  TaskTextArea,
  TaskTextInput,
} from "./taskFormUi";

const InsuranceForm = ({ projectId, onClose, onSave }) => {
  const [insuranceType, setInsuranceType] = useState("Car");
  const [provider, setProvider] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [onedriveLink, setOnedriveLink] = useState("");
  const [existingRecord, setExistingRecord] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!projectId) return;

    const fetchInsurance = async () => {
      const { data } = await taskDetailApi.list("insurance", {
        project_id: projectId,
        insurance_type: insuranceType,
        limit: 1,
      });

      if (data?.[0]) {
        const row = data[0];
        setExistingRecord(row);
        setProvider(row.provider || "");
        setPolicyNumber(row.policy_number || "");
        setStartDate(row.start_date || "");
        setExpiryDate(row.expiry_date || "");
        setNotes(row.notes || "");
        setOnedriveLink(row.onedrive_link || "");
        return;
      }

      setExistingRecord(null);
      setProvider("");
      setPolicyNumber("");
      setStartDate("");
      setExpiryDate("");
      setNotes("");
      setOnedriveLink("");
    };

    fetchInsurance();
  }, [projectId, insuranceType]);

  const handleSave = async (event) => {
    event.preventDefault();
    if (!projectId) return;
    setErrorMessage("");

    const insuranceData = {
      project_id: projectId,
      insurance_type: insuranceType,
      provider,
      policy_number: policyNumber,
      start_date: startDate,
      expiry_date: expiryDate,
      notes,
      onedrive_link: onedriveLink,
    };

    const result =
      existingRecord && existingRecord.insurance_id
        ? await taskDetailApi.update(
            "insurance",
            existingRecord.insurance_id,
            insuranceData,
          )
        : await taskDetailApi.create("insurance", insuranceData);

    if (result.error) {
      console.error("Failed to save insurance details:", result.error);
      setErrorMessage("Failed to save insurance details");
      return;
    }

    onSave?.();
    onClose?.();
  };

  return (
    <TaskDialog
      title="Insurance Details"
      description="Contents or car insurance policy details and supporting link."
      onClose={onClose}
      className="max-w-xl"
    >
      <form onSubmit={handleSave} className="space-y-5">
        <TaskMessage>{errorMessage}</TaskMessage>

        <div className="grid gap-4 sm:grid-cols-2">
          <TaskField
            id="insurance-type"
            label="Insurance Type"
            className="sm:col-span-2"
          >
            <Select value={insuranceType} onValueChange={setInsuranceType}>
              <SelectTrigger id="insurance-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Car">Car Insurance</SelectItem>
                <SelectItem value="Contents">Contents Insurance</SelectItem>
              </SelectContent>
            </Select>
          </TaskField>

          <TaskTextInput
            id="insurance-provider"
            label="Provider"
            value={provider}
            onChange={(event) => setProvider(event.target.value)}
            required
          />
          <TaskTextInput
            id="insurance-policy-number"
            label="Policy Number"
            value={policyNumber}
            onChange={(event) => setPolicyNumber(event.target.value)}
            required
          />
          <TaskTextInput
            id="insurance-start-date"
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            required
          />
          <TaskTextInput
            id="insurance-expiry-date"
            label="Expiry Date"
            type="date"
            value={expiryDate}
            onChange={(event) => setExpiryDate(event.target.value)}
            required
          />
          <TaskTextArea
            id="insurance-notes"
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
            className="sm:col-span-2"
          />
          <TaskTextInput
            id="insurance-link"
            label="OneDrive Link"
            value={onedriveLink}
            onChange={(event) => setOnedriveLink(event.target.value)}
            className="sm:col-span-2"
          />
        </div>

        <TaskDialogActions onCancel={onClose} submitType="submit" />
      </form>
    </TaskDialog>
  );
};

export default InsuranceForm;
